import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { OtpInput, type OtpInputHandle } from './OtpInput';

interface OtpChallengeProps {
  channel: 'email' | 'whatsapp';
  destination: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  expirationSeconds?: number;
  resendCooldownSeconds?: number;
}

function formatMmss(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function OtpChallenge({
  channel,
  destination,
  onVerify,
  onResend,
  expirationSeconds = 600,
  resendCooldownSeconds = 30,
}: OtpChallengeProps) {
  const [code, setCode] = useState('');
  const [expiresIn, setExpiresIn] = useState(expirationSeconds);
  const [resendIn, setResendIn] = useState(resendCooldownSeconds);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<OtpInputHandle>(null);

  useEffect(() => {
    const id = setInterval(() => {
      setExpiresIn((s) => (s > 0 ? s - 1 : 0));
      setResendIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const submit = useCallback(
    async (value: string) => {
      if (submitting) return;
      setSubmitting(true);
      setError(null);
      try {
        await onVerify(value);
      } catch {
        setError('Código inválido ou expirado.');
        inputRef.current?.clear();
      } finally {
        setSubmitting(false);
      }
    },
    [onVerify, submitting],
  );

  const handleResend = useCallback(async () => {
    if (resendIn > 0) return;
    await onResend();
    setResendIn(resendCooldownSeconds);
    setExpiresIn(expirationSeconds);
    setError(null);
  }, [onResend, resendIn, resendCooldownSeconds, expirationSeconds]);

  const channelLabel = channel === 'whatsapp' ? 'WhatsApp' : 'e-mail';

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm text-(--color-fg-muted)">
          Enviamos um código de 6 dígitos para seu {channelLabel}{' '}
          <strong className="text-(--color-fg)">{destination}</strong>. Expira em{' '}
          <span className="tabular-nums">{formatMmss(expiresIn)}</span>.
        </p>
      </div>

      <OtpInput
        ref={inputRef}
        value={code}
        onChange={setCode}
        onComplete={(v) => {
          void submit(v);
        }}
        invalid={!!error}
        disabled={submitting || expiresIn === 0}
        autoFocus
      />

      {error ? (
        <p className="text-sm text-(--color-error)" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-4 text-sm">
        <button
          type="button"
          onClick={handleResend}
          disabled={resendIn > 0}
          className="text-(--color-accent) hover:underline disabled:text-(--color-fg-muted) disabled:no-underline"
        >
          {resendIn > 0 ? `Reenviar em ${resendIn}s` : 'Reenviar código'}
        </button>
        <Button
          type="button"
          onClick={() => void submit(code)}
          disabled={code.length !== 6 || submitting || expiresIn === 0}
        >
          {submitting ? 'Validando…' : 'Verificar'}
        </Button>
      </div>
    </div>
  );
}
